import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

/**
 * Pinned "Top Comment This Week" block — shown on the public Home page.
 *
 * Pulls /api/comments/top-of-week (the most-upvoted visible comment from the
 * last 7 days).  Renders nothing if no comment has any upvotes — so the Home
 * page doesn't show an empty card on a brand-new app.
 */
export default function TopCommentBlock() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [comment, setComment] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/comments/top-of-week")
      .then(({ data }) => {
        if (!cancelled) setComment(data.comment);
      })
      .catch(() => {
        /* silent */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || !comment) return null;

  let dateStr = "";
  try {
    dateStr = new Date(comment.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { /* ignore */ }

  const isPaid = !!user?.has_paid;
  const ctaTo = isPaid ? "/discussion" : (user ? "/paywall" : "/register");
  const ctaText = isPaid ? t("top_comment_cta_paid") : t("top_comment_cta_locked");
  const upvoteLabel = comment.upvote_count === 1
    ? t("top_comment_upvotes_singular")
    : t("top_comment_upvotes_plural");

  return (
    <section className="mt-12 sm:mt-16" data-testid="home-top-comment">
      <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2" style={{ fontWeight: 900 }}>
        <span className="w-2 h-2 bg-[#E00000] rounded-full" />
        {t("top_comment_tag")}
      </div>
      <p className="mt-2 text-black max-w-2xl text-sm sm:text-base" style={{ fontWeight: 600 }}>
        {t("top_comment_intro")}
      </p>

      <blockquote
        className="mt-5 bg-white border-2 border-black p-6 sm:p-8 brutal-shadow-red relative"
        data-testid="home-top-comment-card"
      >
        <span aria-hidden className="absolute -top-4 -left-2 sm:-left-4 font-serif-display text-7xl sm:text-8xl text-[#E00000] leading-none select-none" style={{ fontWeight: 800 }}>
          “
        </span>
        <p className="font-serif-display text-black leading-snug text-xl sm:text-2xl md:text-3xl italic relative z-10" style={{ fontWeight: 700 }} data-testid="home-top-comment-body">
          {comment.body}
        </p>
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 border-t-2 border-black pt-4">
          <div className="font-mono-civic text-xs uppercase tracking-widest text-black" style={{ fontWeight: 900 }}>
            — {comment.author_email_masked}
            {dateStr && <span className="text-black/60 ml-2">· {dateStr}</span>}
          </div>
          <div className="inline-flex items-center gap-1 bg-black text-white px-3 py-1 font-mono-civic text-xs uppercase tracking-widest" style={{ fontWeight: 900 }} data-testid="home-top-comment-upvotes">
            <span aria-hidden>▲</span>
            <span>{comment.upvote_count} {upvoteLabel}</span>
          </div>
        </div>
      </blockquote>

      <div className="mt-5">
        <Link
          to={ctaTo}
          className="inline-flex items-center justify-center bg-black text-white uppercase tracking-widest px-6 py-3 border-2 border-black hover:bg-white hover:text-black transition-colors text-sm"
          style={{ fontWeight: 900 }}
          data-testid="home-top-comment-cta"
        >
          {ctaText}
        </Link>
      </div>
    </section>
  );
}
